// The add-worker flow, in a box.
//
// This is the compact consumer of useAddWorkerWizard: a breadcrumb strip, a
// heading, the step body and a footer, all sized to sit inside something else.
// The setup flow embeds it in step 4, where a full WizardShell would be a shell
// inside a shell. The standalone version is pages/workers/new.tsx.

import { Button } from "@veil/ui/components/button";
import { AnimatePresence } from "motion/react";
import { StepTransition } from "@/components/wizard/step-transition";
import {
	type AddWorkerWizardOptions,
	useAddWorkerWizard,
} from "./use-add-worker-wizard";

export function AddWorkerWizard({
	onStarted,
	onRegistered,
	onCancel,
}: Pick<AddWorkerWizardOptions, "onStarted" | "onRegistered"> & {
	onCancel?: () => void;
}) {
	const { wizard, meta, stepNode, submit, submitLabel, pending } =
		useAddWorkerWizard({ onStarted, onRegistered });

	return (
		<form onSubmit={submit} className="flex min-h-0 flex-col gap-5">
			<ol className="flex items-center gap-2 text-xs" aria-label="Progress">
				{wizard.steps.map((s, i) => (
					<li key={s.id} className="flex items-center gap-2">
						<span
							className={
								i === wizard.currentIndex
									? "font-medium text-foreground"
									: "text-muted-foreground"
							}
							aria-current={i === wizard.currentIndex ? "step" : undefined}
						>
							{s.label}
						</span>
						{i < wizard.steps.length - 1 ? (
							<span className="text-muted-foreground/50" aria-hidden="true">
								/
							</span>
						) : null}
					</li>
				))}
			</ol>

			<div>
				<h2 className="text-base font-semibold">{meta.title}</h2>
				<p className="mt-0.5 text-sm text-muted-foreground">{meta.blurb}</p>
			</div>

			{/* Keyed by step id so each body animates in as the previous leaves. */}
			<AnimatePresence mode="wait" initial={false}>
				<StepTransition key={wizard.current.id}>{stepNode}</StepTransition>
			</AnimatePresence>

			<div className="mt-auto flex items-center justify-between gap-2 border-t pt-4">
				<Button
					type="button"
					variant="ghost"
					onClick={wizard.isFirst ? onCancel : wizard.back}
					disabled={pending || (wizard.isFirst && !onCancel)}
				>
					{wizard.isFirst ? "Cancel" : "Back"}
				</Button>
				{wizard.isLast ? (
					<Button type="submit" disabled={pending}>
						{submitLabel}
					</Button>
				) : (
					<Button
						type="button"
						onClick={() => {
							void wizard.next();
						}}
					>
						Continue
					</Button>
				)}
			</div>
		</form>
	);
}
